import { useCallback } from 'react';
import { API_CONFIG, getAuthHeadersWithoutCredentials } from '../constants/api';
import { useSoundworldsContext, Soundworld } from '../contexts/SoundworldsContext';
// import { useWebSocket } from './useWebSocket';

export interface GeneratedPiece {
    piece_id: string;
    url?: string;
    ready: boolean;
    error?: string;
    comment?: string;
}

export interface SoundworldGeneration {
    piece_id: string;
    url?: string;
    ready: boolean;
    error?: string;
    comment?: string;
    created_at?: string;
    tags?: string[];
    soundworld?: string;
}

export const useSoundworlds = () => {
    const { soundworlds, isLoading, error, hasLoaded } = useSoundworldsContext();
    // const { session_id: webSocketSessionId, isConnected: isWebSocketConnected } = useWebSocket();

    const generatePreview = useCallback(async (tags: string[]) => {
        try {
            // if (!isWebSocketConnected) {
            //     throw new Error('WebSocket is not connected');
            // }
            const response = await fetch(`${API_CONFIG.BASE_URL}/soundworld/preview`, {
                method: 'POST',
                headers: getAuthHeadersWithoutCredentials(),
                body: JSON.stringify({
                    tags: tags,
                })
            });

            if (!response.ok) {
                throw new Error('Failed to generate preview');
            }

            const result: GeneratedPiece = await response.json();
            return result;
        } catch (err) {
            throw err;
        }
    }, []);

    const getMidiUrl = useCallback(async (pieceId: string) => {
        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}/soundworld/get_midi_url?piece_id=${pieceId}`, {
                method: 'GET',
                headers: getAuthHeadersWithoutCredentials(),
            });

            if (!response.ok) {
                throw new Error('Failed to get MIDI URL');
            }

            const result = await response.json();
            return result.url;
        } catch (err) {
            throw err;
        }
    }, []);

    const renderStems = useCallback(async (jobId: string, stemOptions?: any) => {
        try {
            // if (!isWebSocketConnected) {
            //     throw new Error('WebSocket is not connected');
            // }
            const body: any = {
                job_id: jobId,
                stem_options: stemOptions
            };

            const sessionId = localStorage.getItem('session_id');
            if (sessionId) {
                body.session_id = sessionId;
            }

            const response = await fetch(`${API_CONFIG.BASE_URL}/soundworld/render_stems`, {
                method: 'POST',
                headers: getAuthHeadersWithoutCredentials(),
                body: JSON.stringify(body)
            });

            if (!response.ok) {
                throw new Error('Failed to render stems');
            }

            const result = await response.json();
            return result;
        } catch (err) {
            throw err;
        }
    }, []);

    const renderStemsByPieceId = useCallback(async (pieceId: string) => {
        try {
            // if (!isWebSocketConnected) {
            //     throw new Error('WebSocket is not connected');
            // }
            const params = new URLSearchParams({
                piece_id: pieceId
            });

            const sessionId = localStorage.getItem('session_id');// || webSocketSessionId;

            if (sessionId) {
                params.append('session_id', sessionId);
            } else {
                console.error('No session_id available');
                throw new Error('No session_id available for stems rendering');
            }

            const url = `${API_CONFIG.BASE_URL}/soundworld/render_stems?${params.toString()}`;
            console.log('renderStemsByPieceId - URL:', url);

            const response = await fetch(url, {
                method: 'GET',
                headers: getAuthHeadersWithoutCredentials(),
            });

            if (!response.ok) {
                throw new Error('Failed to render stems');
            }

            const result = await response.json();
            return result;
        } catch (err) {
            throw err;
        }
    }, []);

    const getClientGenerations = useCallback(async (): Promise<string[]> => {
        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SOUNDWORLD_GET_CLIENT_GENERATIONS}`, {
                method: 'GET',
                headers: getAuthHeadersWithoutCredentials(),
            });

            if (!response.ok) {
                throw new Error('Failed to get client generations');
            }

            const result = await response.json();
            return result.piece_ids || [];
        } catch (err) {
            throw err;
        }
    }, []);

    const getGeneration = useCallback(async (pieceId: string): Promise<SoundworldGeneration> => {
        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SOUNDWORLD_GET_GENERATION}?piece_id=${pieceId}`, {
                method: 'GET',
                headers: getAuthHeadersWithoutCredentials(),
            });

            if (!response.ok) {
                throw new Error('Failed to get generation');
            }

            const result = await response.json();
            return result;
        } catch (err) {
            throw err;
        }
    }, []);

    return {
        soundworlds,
        isLoading,
        error,
        hasLoaded,
        renderStemsByPieceId,
        generatePreview,
        getMidiUrl,
        renderStems,
        getClientGenerations,
        getGeneration,
    };
};